// Phase 72 / 83: dispatcher + field renderers for admin-defined
// CustomFields. Self-contained: each renderer reads/writes a single
// value via the (value, onChange) props. The dispatcher routes by
// field.fieldType.
//
// Field types rendered (Phase 83 completes the declared set):
//   - short_text    single-line text input (name, monogram, etc.)
//   - long_text     multi-line textarea (instructions, notes)
//   - select        dropdown of options (most common pattern)
//   - radio         radio-button group (single pick, tap selects)
//   - checkboxes    multi-select checkbox group (value is string[])
//   - swatch        round colour dots (option.swatchHex, or imageUrl
//                   thumbnails when set)
//   - image_swatch  thumbnail tile grid (visual picker)
//   - date          native date picker
//
// Still pending (file): the dispatcher falls through to an
// admin-visible "Unsupported field type: X" notice so the merchant
// knows the field won't render until support lands.

import * as React from "react";
import type { CustomField, CustomFieldOption } from "../../types";

/** "+$X" price chip beside an option, matching the image-swatch price
 *  treatment. Renders nothing when the option carries no surcharge. */
function OptionPriceChip({
  opt,
  mode,
}: {
  opt: CustomFieldOption;
  /** The PARENT field's priceImpactMode. Required: pricing.ts charges 0 for
   *  every mode except per_selection, so showing a chip from opt.priceCents
   *  alone advertises a surcharge that is never taken. */
  mode: CustomField["priceImpactMode"];
}) {
  if (mode !== "per_selection") return null;
  if (!opt.priceCents) return null;
  return (
    <span className="rk-pc-field__opt-price">
      +${(opt.priceCents / 100).toFixed(0)}
    </span>
  );
}

export type FieldValue = string | string[];

type Props = {
  field: CustomField;
  value: FieldValue | undefined;
  onChange: (value: FieldValue) => void;
};

/** Single-line text input. Value is always a string. */
function ShortTextField({ field, value, onChange }: Props) {
  const str = typeof value === "string" ? value : "";
  return (
    <input
      type="text"
      className="rk-pc-field__text"
      value={str}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.defaultValue ?? ""}
      maxLength={field.maxLength ?? undefined}
      minLength={field.minLength ?? undefined}
      required={field.required}
      aria-label={field.label}
    />
  );
}

/** Multi-line textarea. Same value contract as ShortTextField. */
function LongTextField({ field, value, onChange }: Props) {
  const str = typeof value === "string" ? value : "";
  return (
    <textarea
      className="rk-pc-field__textarea"
      value={str}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.defaultValue ?? ""}
      maxLength={field.maxLength ?? undefined}
      minLength={field.minLength ?? undefined}
      rows={3}
      required={field.required}
      aria-label={field.label}
    />
  );
}

/** Dropdown. Value is the picked option's `value` string. */
function SelectField({ field, value, onChange }: Props) {
  const str = typeof value === "string" ? value : "";
  return (
    <select
      className="rk-pc-field__select"
      value={str}
      onChange={(e) => onChange(e.target.value)}
      required={field.required}
      aria-label={field.label}
    >
      {/* Empty first option for "no selection" UX; required gate forbids it. */}
      {!field.required && <option value="">(no selection)</option>}
      {field.options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
          {opt.priceCents ? ` (+$${(opt.priceCents / 100).toFixed(0)})` : ""}
        </option>
      ))}
    </select>
  );
}

/** Image-backed tile grid. Picked option's `value` is stored. Visual
 *  treatment matches the atelier aesthetic (cream tile, rounded, accent
 *  border on select). */
function ImageSwatchField({ field, value, onChange }: Props) {
  const str = typeof value === "string" ? value : "";
  return (
    <div className="rk-pc-field__image-swatch-grid">
      {field.options.map((opt) => {
        const selected = str === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            className={`rk-pc-field__image-swatch-tile${selected ? " is-selected" : ""}`}
            onClick={() => onChange(opt.value)}
            aria-pressed={selected}
            aria-label={opt.label}
          >
            {opt.imageUrl ? (
              <img
                src={opt.imageUrl}
                alt={opt.label}
                className="rk-pc-field__image-swatch-img"
              />
            ) : (
              <span className="rk-pc-field__image-swatch-placeholder">
                {opt.label.slice(0, 2).toUpperCase()}
              </span>
            )}
            <span className="rk-pc-field__image-swatch-label">{opt.label}</span>
            {opt.priceCents ? (
              <span className="rk-pc-field__image-swatch-price">
                +${(opt.priceCents / 100).toFixed(0)}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/** Radio-button group. Single pick; a tap selects (no confirm step,
 *  matching how the other single-select field types commit instantly). */
function RadioGroupField({ field, value, onChange }: Props) {
  const str = typeof value === "string" ? value : "";
  return (
    <div
      className="rk-pc-field__radio-group"
      role="radiogroup"
      aria-label={field.label}
    >
      {field.options.map((opt) => {
        const selected = str === opt.value;
        return (
          <label
            key={opt.value}
            className={`rk-pc-field__choice-row${selected ? " is-selected" : ""}`}
          >
            <input
              type="radio"
              className="rk-pc-field__choice-input"
              name={`rk-pc-field-${field.id}`}
              value={opt.value}
              checked={selected}
              onChange={() => onChange(opt.value)}
              required={field.required}
            />
            <span className="rk-pc-field__choice-label">{opt.label}</span>
            <OptionPriceChip opt={opt} mode={field.priceImpactMode} />
          </label>
        );
      })}
    </div>
  );
}

/** Checkbox group. Multi-select; value is ALWAYS a string[] (ticked
 *  option values in tick order). Pricing's per_selection mode stacks
 *  each ticked option's priceCents. */
function CheckboxGroupField({ field, value, onChange }: Props) {
  const arr = Array.isArray(value) ? value : [];
  const toggle = (optValue: string) => {
    onChange(
      arr.includes(optValue)
        ? arr.filter((v) => v !== optValue)
        : [...arr, optValue],
    );
  };
  return (
    <div className="rk-pc-field__checkbox-group" role="group" aria-label={field.label}>
      {field.options.map((opt) => {
        const selected = arr.includes(opt.value);
        return (
          <label
            key={opt.value}
            className={`rk-pc-field__choice-row${selected ? " is-selected" : ""}`}
          >
            <input
              type="checkbox"
              className="rk-pc-field__choice-input"
              value={opt.value}
              checked={selected}
              onChange={() => toggle(opt.value)}
            />
            <span className="rk-pc-field__choice-label">{opt.label}</span>
            <OptionPriceChip opt={opt} mode={field.priceImpactMode} />
          </label>
        );
      })}
    </div>
  );
}

/** Hex swatch grid. Round colour dots backed by option.swatchHex; when
 *  an option carries an imageUrl instead, the dot renders the image as
 *  a thumbnail (same footprint). Tap selects. Visual treatment follows
 *  the thread-picker dots. */
function HexSwatchField({ field, value, onChange }: Props) {
  const str = typeof value === "string" ? value : "";
  return (
    <div className="rk-pc-field__swatch-grid" role="radiogroup" aria-label={field.label}>
      {field.options.map((opt) => {
        const selected = str === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            className={`rk-pc-field__swatch${selected ? " is-selected" : ""}`}
            onClick={() => onChange(opt.value)}
            aria-pressed={selected}
            aria-label={opt.label}
            title={opt.label}
          >
            {opt.imageUrl ? (
              <img
                src={opt.imageUrl}
                alt=""
                className="rk-pc-field__swatch-img"
              />
            ) : (
              <span
                className="rk-pc-field__swatch-dot"
                style={{ background: opt.swatchHex || "#e5e7eb" }}
              />
            )}
            <span className="rk-pc-field__swatch-label">{opt.label}</span>
            <OptionPriceChip opt={opt} mode={field.priceImpactMode} />
          </button>
        );
      })}
    </div>
  );
}

/** Native date picker. Value is the input's YYYY-MM-DD string (what
 *  rides onto the cart property). The CustomField schema carries no
 *  min/max date columns, so no range clamp is applied here. */
function DateField({ field, value, onChange }: Props) {
  const str = typeof value === "string" ? value : "";
  return (
    <input
      type="date"
      className="rk-pc-field__date"
      value={str}
      onChange={(e) => onChange(e.target.value)}
      required={field.required}
      aria-label={field.label}
    />
  );
}

/** Fallback for field types not yet supported in v1. Shows a small
 *  cream-coloured notice so the merchant sees their field is configured
 *  but not yet renderable. Customers see the same notice; field is
 *  treated as "not set" for pricing + cart property emission. */
function UnsupportedFieldType({ field }: { field: CustomField }) {
  return (
    <div
      className="rk-pc-field__unsupported"
      style={{
        border: "1px dashed #d9c7b3",
        background: "#fdf8f1",
        color: "#7a5a3a",
        padding: "8px 12px",
        borderRadius: 8,
        fontSize: 13,
      }}
    >
      Field type <code>{field.fieldType}</code> is configured in the
      admin but not yet renderable in this version of the customiser.
    </div>
  );
}

/** Top-level dispatcher. Renders the field's label + help text + the
 *  type-specific input. Used by CustomFieldsSection AND by
 *  PlacementSection (for with_spot placements). */
export function CustomFieldRenderer({ field, value, onChange }: Props) {
  let inner: React.ReactNode;
  switch (field.fieldType) {
    case "short_text":
      inner = <ShortTextField field={field} value={value} onChange={onChange} />;
      break;
    case "long_text":
      inner = <LongTextField field={field} value={value} onChange={onChange} />;
      break;
    case "select":
      inner = <SelectField field={field} value={value} onChange={onChange} />;
      break;
    case "radio":
      inner = <RadioGroupField field={field} value={value} onChange={onChange} />;
      break;
    case "checkboxes":
      inner = <CheckboxGroupField field={field} value={value} onChange={onChange} />;
      break;
    case "swatch":
      inner = <HexSwatchField field={field} value={value} onChange={onChange} />;
      break;
    case "date":
      inner = <DateField field={field} value={value} onChange={onChange} />;
      break;
    case "image_swatch":
      inner = <ImageSwatchField field={field} value={value} onChange={onChange} />;
      break;
    default:
      inner = <UnsupportedFieldType field={field} />;
  }

  return (
    // data-rk-field-slug is the scroll + pulse target for a blocked
    // add-to-cart tap (App.tsx scrollToBlocking / guideBlockedAtc). Without a
    // stable hook the guidance falls through to the top of the section and
    // the tap reads as dead.
    <div
      className={`rk-pc-field rk-pc-field--${field.fieldType}`}
      data-rk-field-slug={field.slug}
    >
      <label className="rk-pc-field__label">
        {field.label}
        {field.required && (
          <span className="rk-pc-field__required" aria-hidden="true">
            {" *"}
          </span>
        )}
      </label>
      {field.helpText && (
        <p className="rk-pc-field__hint">{field.helpText}</p>
      )}
      {inner}
    </div>
  );
}

/** Format a field's current value into a customer-facing summary string,
 *  used inside the CustomFieldsSection header. */
export function summariseFieldValue(
  field: CustomField,
  value: FieldValue | undefined,
): string {
  if (value == null) return "";
  if (Array.isArray(value)) {
    return value
      .map((v) => labelForOptionValue(field, v))
      .filter(Boolean)
      .join(", ");
  }
  if (!value) return "";
  if (
    field.fieldType === "select" ||
    field.fieldType === "radio" ||
    field.fieldType === "image_swatch" ||
    field.fieldType === "swatch"
  ) {
    return labelForOptionValue(field, value);
  }
  return value;
}

function labelForOptionValue(
  field: CustomField,
  value: string,
): string {
  const opt: CustomFieldOption | undefined = field.options.find(
    (o) => o.value === value,
  );
  return opt?.label ?? value;
}
